import type { RailSuggestion } from './cards/SuggestionsCard';
import type { TrustSignal } from './cards/TrustSignalsCard';
import type { TopicCoverageItem } from './cards/TopicCoverageCard';
import type { InternalLinkSuggestion } from './cards/InternalLinksCard';
import type { ImageSuggestion } from './cards/ImageSuggestionsCard';
import type { StructureItem } from './cards/StructureChecklistCard';
import type { SchemaType } from './cards/SchemaTypesCard';
import type { RailComment } from './CommentsList';
import type { AskAIMessage, AskAIPreset } from './AskAIChat';
import type { ContentQualityCardProps } from './cards/ContentQualityCard';
import type { SerpPreviewData } from './cards/SerpPreviewCard';

/**
 * Mock data mirroring the design package (editor-rail.jsx + editor-cards.jsx).
 * Each export is meant to be imported by stories, T5 wiring, or any temporary
 * preview that hasn't been hooked up to a real service yet. NEVER ship this
 * data in production paths — the rail components are fully prop-driven.
 */

export const DEMO_SUGGESTIONS: RailSuggestion[] = [
  {
    id: 'sug-1',
    kind: 'tone',
    text: 'really really long-lasting',
    fix: 'durable',
    reason: 'Avoid intensifier stacking; "durable" carries the same meaning.',
  },
  {
    id: 'sug-2',
    kind: 'fact',
    text: 'lasts 50 years',
    fix: 'lasts 15–20 years (per APA 2024)',
    reason: 'No source backs the 50-year claim. Adjusted to APA range.',
  },
  {
    id: 'sug-3',
    kind: 'clarity',
    text: 'utilize concrete footings',
    fix: 'use concrete footings',
    reason: 'Plain-language preference.',
  },
];

export const DEMO_TRUST_SIGNALS: TrustSignal[] = [
  { id: 'reality', label: 'Reality Filter', status: 'All claims verified', value: '24/24', tone: 'success' },
  { id: 'authority', label: 'Authority Links', status: 'Add 1 more citation', value: '4/5', tone: 'warning' },
  { id: 'temporal', label: 'Temporal Relevancy', status: 'All references current', value: '2026', tone: 'success' },
];

export const DEMO_COVERAGE: TopicCoverageItem[] = [
  { keyword: 'wood fence', used: 5, target: 5, sv: 8100, kd: 32, cpc: 2.4, primary: true },
  { keyword: 'fence lifespan', used: 2, target: 3, sv: 1300, kd: 24, cpc: 1.8 },
  { keyword: 'cedar fence', used: 3, target: 3, sv: 4400, kd: 38, cpc: 2.7 },
  { keyword: 'pressure treated', used: 1, target: 3, sv: 2900, kd: 29, cpc: 2.1 },
  { keyword: 'fence post rot', used: 0, target: 2, sv: 880, kd: 18, cpc: 1.4 },
  { keyword: 'shadowbox fence', used: 1, target: 2, sv: 1900, kd: 22, cpc: 2.3 },
];

export const DEMO_INTERNAL_LINKS: InternalLinkSuggestion[] = [
  {
    id: 'il-1',
    anchor: 'pressure-treated pine',
    target: 'How to seal pressure-treated wood',
    section: 'Species comparison',
    confidence: 0.92,
  },
  {
    id: 'il-2',
    anchor: 'concrete footings',
    target: 'Best concrete mix for fence posts',
    section: 'How long does it really last',
    confidence: 0.87,
  },
  {
    id: 'il-3',
    anchor: 'cedar resists insects',
    target: 'Cedar vs redwood: 2026 cost comparison',
    section: 'Species comparison',
    confidence: 0.81,
  },
  {
    id: 'il-4',
    anchor: 'shadowbox designs',
    target: 'Privacy fence styles ranked by lifespan',
    section: 'Key Takeaways',
    confidence: 0.74,
  },
];

export const DEMO_IMAGE_SUGGESTIONS: ImageSuggestion[] = [
  {
    id: 'img-1',
    section: 'Species comparison',
    alt: 'Cedar vs pressure-treated grain comparison',
    placement: 'After paragraph 2',
  },
  {
    id: 'img-2',
    section: 'How long does a wood fence really last?',
    alt: 'Cross-section of fence post in concrete footing',
    placement: 'After paragraph 1',
  },
  {
    id: 'img-3',
    section: 'Key Takeaways',
    alt: 'Diagram showing fence post drainage layer',
    placement: 'Above bullet 2',
  },
];

export const DEMO_STRUCTURE: StructureItem[] = [
  { id: 's-1', tag: 'H1', title: 'How Long Does a Wood Fence Last?' },
  { id: 's-2', tag: 'H2', title: 'Key Takeaways' },
  { id: 's-3', tag: 'H2', title: 'How long does a wood fence really last?' },
  { id: 's-4', tag: 'H3', title: 'Species comparison' },
  { id: 's-5', tag: 'H2', title: 'Maintenance schedule', flagged: 'missing' },
  { id: 's-6', tag: 'H2', title: 'When to replace vs repair', flagged: 'missing' },
];

export const DEMO_SCHEMAS: SchemaType[] = [
  { id: 'article', label: 'Article', applied: true },
  { id: 'howto', label: 'HowTo', applied: true },
  { id: 'faq', label: 'FAQPage', applied: false },
  { id: 'breadcrumb', label: 'BreadcrumbList', applied: true },
  { id: 'product', label: 'Product', applied: false },
];

export const DEMO_QUALITY: Omit<ContentQualityCardProps, 'judge'> & {
  judge: ContentQualityCardProps['judge'];
} = {
  overallScore: 88,
  grade: 'B+',
  delta: '+5 since last analysis',
  pills: [
    { label: 'SEO', score: 91 },
    { label: 'Authority', score: 76 },
    { label: 'Structure', score: 94 },
  ],
  seo: {
    score: 91,
    findings: [
      { text: 'Focus keyword present in title, H1, first 100 words ✓' },
      { text: 'Meta description optimized ✓' },
      { text: 'Add 2 more LSI variations of "fence post"', warning: true },
    ],
  },
  authority: {
    score: 76,
    findings: [
      { text: '4 of 5 outbound citations to .gov/.edu sources ✓' },
      { text: 'Add 1 more authority citation to meet threshold', warning: true },
    ],
  },
  structure: {
    score: 94,
    findings: [
      { text: 'H1 / H2 / H3 hierarchy correct ✓' },
      { text: 'Average paragraph length: 3.2 sentences ✓' },
      { text: 'Bulleted summary present ✓' },
    ],
  },
  judge: {
    score: 82,
    summary: 'Original voice score: 92. Helpful score: 88. Reads like a contractor wrote it.',
  },
};

export const DEMO_SERP: SerpPreviewData = {
  title: 'How Long Does a Wood Fence Last? Lifespan + Maintenance Tips',
  description:
    'Wood fences last 15–20 years on average. Learn what affects lifespan and how to extend it with proper maintenance.',
  url: 'aaafence.com › blog',
};

export const DEMO_COMMENTS: RailComment[] = [
  {
    id: 'c-1',
    authorName: 'Jeff Patterson',
    body: 'Can we add a callout for the cedar option here? Most readers will skim past.',
    createdAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
    quote: 'Cedar resists insects and rot naturally',
    replies: [
      {
        id: 'r-1',
        authorName: 'Carl Cayton',
        body: 'Good call — adding a Pill block above this paragraph.',
        createdAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
      },
    ],
  },
  {
    id: 'c-2',
    authorName: 'Jacob Lin',
    body: 'Source for the 50-year claim?',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    quote: 'Some installations have lasted 50 years',
  },
  {
    id: 'c-3',
    authorName: 'Arian',
    body: 'Resolved — swapped to APA citation.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    resolved: true,
  },
];

export const DEMO_AI_MESSAGES: AskAIMessage[] = [];

export const DEMO_AI_PRESETS: AskAIPreset[] = [
  { id: 'tldr', label: 'TL;DR this article', prompt: 'Give me a one-paragraph TL;DR of the article.' },
  {
    id: 'meta',
    label: 'Suggest meta description',
    prompt: 'Write a 155-character meta description for this article.',
  },
  {
    id: 'tighten',
    label: 'Tighten intro',
    prompt: 'Rewrite the introduction so it is 30% shorter without losing the hook.',
  },
  {
    id: 'fact',
    label: 'Fact-check claims',
    prompt: 'List the load-bearing factual claims and flag anything unverifiable.',
  },
];
